import {
  PerformanceReportRecord,
  ReportPhase,
  SanitizedClsContributor,
  SanitizedCoreWebVitals,
  SanitizedExternalScript,
  SanitizedFrustrationPoint,
  SanitizedLongTaskRecord,
  SanitizedNavigationTiming,
  SanitizedPerformanceReport
} from './performanceReportTypes';

const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000;
const LCP_THRESHOLD = 2500;
const CLS_THRESHOLD = 0.1;
const FID_THRESHOLD = 100;
const FCP_THRESHOLD = 1800;
const TTFB_THRESHOLD = 800;
const TBT_THRESHOLD = 200;
const MAX_CONTEXT_LENGTH = 200;
const MAX_CLASSES = 3;

interface RawMetrics {
  lcp?: number | null;
  cls?: number | null;
  fid?: number | null;
  fcp?: number | null;
  ttfb?: number | null;
}

let visitIdCounter = 0;

export const createVisitId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const values = new Uint32Array(2);
    crypto.getRandomValues(values);
    return `${values[0].toString(16)}${values[1].toString(16)}`;
  }

  visitIdCounter++;
  return `${Date.now().toString(16)}-${visitIdCounter.toString(16)}`;
};

export const createRouteSlug = (path: string): string => {
  let slug = path.toLowerCase().replace(/[^a-z0-9]+/g, '-');

  let start = 0;
  let end = slug.length;
  while (start < end && slug[start] === '-') start++;
  while (end > start && slug[end - 1] === '-') end--;
  slug = slug.slice(start, end);

  return slug || 'home';
};

export const createReportId = (routeSlug: string, timestamp: number, visitId: string, phase: ReportPhase): string =>
  `${routeSlug}__${timestamp}__${visitId}__${phase}`;

export const sanitizeUrl = (url: string, pageOrigin: string): string => {
  try {
    const parsed = new URL(url, pageOrigin);
    const sanitized = `${parsed.origin}${parsed.pathname}`;
    return sanitized;
  } catch {
    return url.split('?')[0].split('#')[0];
  }
};

export const shouldPersistReport = (metrics: RawMetrics, tbt: number, frustrationCount: number): boolean =>
  (metrics.lcp ?? 0) > LCP_THRESHOLD ||
  (metrics.cls ?? 0) > CLS_THRESHOLD ||
  (metrics.fid ?? 0) > FID_THRESHOLD ||
  (metrics.fcp ?? 0) > FCP_THRESHOLD ||
  (metrics.ttfb ?? 0) > TTFB_THRESHOLD ||
  tbt > TBT_THRESHOLD ||
  frustrationCount > 0;

const isNonEmptyString = (value: unknown): value is string => typeof value === 'string' && value.length > 0;

const toDisplayString = (value: unknown, fallback: string): string => {
  if (isNonEmptyString(value)) return value;
  if (typeof value === 'number' && Number.isFinite(value)) return `${value}`;
  return fallback;
};

const sanitizePagePath = (value: string, pageOrigin: string): string => {
  let path = value;
  if (path.startsWith(pageOrigin)) {
    path = path.slice(pageOrigin.length);
  }
  return path.split('?')[0].split('#')[0] || '/';
};

export const sanitizeReportContext = (raw: Record<string, unknown>, pageOrigin: string): Record<string, string> => {
  const allowed: Record<string, string> = {};
  const keys = ['page', 'environment', 'buildVersion', 'connection', 'collectedAt'];
  for (const key of keys) {
    const value = raw[key];
    allowed[key] = isNonEmptyString(value) ? value : 'Unavailable';
  }
  allowed.page = sanitizePagePath(allowed.page, pageOrigin);
  return allowed;
};

export const sanitizeNavigationTiming = (raw: Record<string, unknown>): SanitizedNavigationTiming => {
  const get = (key: string): string => {
    const value = raw[key];
    return typeof value === 'string' || typeof value === 'number' ? String(value) : 'n/a';
  };
  return {
    dnsLookup: get('dnsLookup'),
    tcpConnection: get('tcpConnection'),
    requestResponse: get('requestResponse'),
    responseDownload: get('responseDownload'),
    domProcessing: get('domProcessing'),
    pageLoad: get('pageLoad'),
    totalTime: get('totalTime')
  };
};

const parseCustomMetrics = (raw: unknown): Record<string, number> => {
  const metrics: Record<string, number> = {};
  if (typeof raw === 'object' && raw !== null) {
    for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
      if (typeof value === 'number' && Number.isFinite(value)) {
        metrics[key] = value;
      }
    }
  }
  return metrics;
};

export const sanitizeCoreWebVitals = (raw: Record<string, unknown>): SanitizedCoreWebVitals => {
  const toNumber = (value: unknown): number | null =>
    typeof value === 'number' && Number.isFinite(value) ? value : null;
  return {
    lcp: toNumber(raw.lcp),
    fid: toNumber(raw.fid),
    cls: toNumber(raw.cls),
    ttfb: toNumber(raw.ttfb),
    fcp: toNumber(raw.fcp),
    customMetrics: parseCustomMetrics(raw.customMetrics)
  };
};

export const sanitizeLongTaskContext = (context: string): string => {
  if (!isNonEmptyString(context)) return 'Unavailable';
  return context
    .replace(/#[\w-]+/g, '#<id>')
    .replace(/\.[\w-]+/g, '.<class>')
    .slice(0, MAX_CONTEXT_LENGTH);
};

export const sanitizeLongTasks = (raw: unknown[], pageOrigin: string): SanitizedLongTaskRecord[] => {
  const tasks: SanitizedLongTaskRecord[] = [];
  for (const item of raw) {
    if (typeof item !== 'object' || item === null) continue;
    const record = item as Record<string, unknown>;
    const duration = typeof record.duration === 'number' ? record.duration : 0;
    const startTime = typeof record.startTime === 'number' ? record.startTime : 0;
    const blockingTime = Math.max(0, duration - 50);
    const attribution = isNonEmptyString(record.attribution) ? sanitizeUrl(record.attribution, pageOrigin) : 'Unavailable';
    const context = typeof record.context === 'string' ? record.context : '';
    tasks.push({
      startTime,
      endTime: startTime + duration,
      duration,
      blockingTime,
      context: sanitizeLongTaskContext(context),
      attribution
    });
  }
  return tasks;
};

export const sanitizeExternalScripts = (
  raw: unknown[],
  pageOrigin: string
): SanitizedExternalScript[] => {
  const scripts: SanitizedExternalScript[] = [];
  for (const item of raw) {
    if (typeof item !== 'object' || item === null) continue;
    const record = item as Record<string, unknown>;
    const rawUrl = isNonEmptyString(record.script) ? record.script : '';
    const sanitizedUrl = sanitizeUrl(rawUrl, pageOrigin);
    const origin = (() => {
      try {
        return new URL(sanitizedUrl, pageOrigin).origin;
      } catch {
        return pageOrigin;
      }
    })();
    const party = origin === pageOrigin ? 'First-party' : 'Third-party';
    scripts.push({
      script: sanitizedUrl,
      origin,
      party,
      startTime: toDisplayString(record.startTime, 'n/a'),
      loadDuration: toDisplayString(record.loadDuration, 'n/a'),
      transferSize: toDisplayString(record.transferSize, 'Unavailable'),
      overlapsLongTask: Boolean(record.overlapsLongTask)
    });
  }
  return scripts;
};

export const sanitizeClsContributors = (raw: unknown[]): SanitizedClsContributor[] => {
  const contributors: SanitizedClsContributor[] = [];
  for (const item of raw) {
    if (typeof item !== 'object' || item === null) continue;
    const record = item as Record<string, unknown>;
    contributors.push({
      score: typeof record.score === 'number' ? record.score : 0,
      startTime: typeof record.startTime === 'number' ? record.startTime : 0,
      element: isNonEmptyString(record.element) ? record.element : 'Unknown element'
    });
  }
  return contributors;
};

const sanitizeElementDescriptor = (value: string): string =>
  value
    .replace(/#[\w-]+/g, '#<id>')
    .replace(/\.[\w-]+/g, '.<class>');

export const sanitizeFrustrationPoints = (raw: unknown[]): SanitizedFrustrationPoint[] => {
  const points: SanitizedFrustrationPoint[] = [];
  for (const item of raw) {
    if (typeof item !== 'object' || item === null) continue;
    const record = item as Record<string, unknown>;
    const affectedElements = isNonEmptyString(record.affectedElements)
      ? record.affectedElements
      : 'Page-level or unavailable';
    points.push({
      category: isNonEmptyString(record.category) ? record.category : 'unknown',
      events: typeof record.events === 'number' ? record.events : 0,
      totalScore: typeof record.totalScore === 'number' ? record.totalScore : 0,
      affectedElements: sanitizeElementDescriptor(affectedElements.split(',').slice(0, MAX_CLASSES).join(',')),
      lastOccurrence: isNonEmptyString(record.lastOccurrence) ? record.lastOccurrence : 'Unavailable'
    });
  }
  return points;
};

export const sanitizePerformanceReport = (
  raw: Record<string, unknown>,
  pageOrigin: string
): SanitizedPerformanceReport => ({
  context: sanitizeReportContext((raw.context as Record<string, unknown>) ?? {}, pageOrigin),
  navigation: sanitizeNavigationTiming((raw.navigation as Record<string, unknown>) ?? {}),
  coreWebVitals: sanitizeCoreWebVitals((raw.coreWebVitals as Record<string, unknown>) ?? {}),
  tbt: typeof raw.tbt === 'number' ? raw.tbt : 0,
  tbtWindow: typeof raw.tbtWindow === 'object' && raw.tbtWindow !== null
    ? raw.tbtWindow as { start: number | null; end: number }
    : { start: null, end: Date.now() },
  longTasks: sanitizeLongTasks(raw.longTasks as unknown[] ?? [], pageOrigin),
  externalScripts: sanitizeExternalScripts(raw.externalScripts as unknown[] ?? [], pageOrigin),
  clsContributors: sanitizeClsContributors(raw.clsContributors as unknown[] ?? []),
  frustrationPoints: sanitizeFrustrationPoints(raw.frustrationPoints as unknown[] ?? []),
  monitoringStatus: typeof raw.monitoringStatus === 'object' && raw.monitoringStatus !== null
    ? Object.fromEntries(Object.entries(raw.monitoringStatus as Record<string, unknown>)
      .map(([key, value]) => [key, typeof value === 'string' || typeof value === 'number' ? value : 'Unavailable']))
    : {}
});

export const createPersistedReport = (
  path: string,
  visitId: string,
  phase: ReportPhase,
  timestamp: number,
  report: SanitizedPerformanceReport
): PerformanceReportRecord => {
  const urlName = path.split('?')[0].split('#')[0] || '/';
  const routeSlug = createRouteSlug(urlName);
  return {
    id: createReportId(routeSlug, timestamp, visitId, phase),
    urlName,
    routeSlug,
    timestamp,
    visitId,
    phase,
    expiresAt: timestamp + ONE_YEAR_MS,
    report
  };
};
